import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuthStore } from "../store/authStore";
import { renderInlineMarkdown } from "../lib/markdown";
import { useT } from "../i18n";

const LS_KEY = "itf_last_seen_version";

interface VersionInfo {
  current_version: string;
  current_date: string;
  current_highlights: string[];
}

export default function WhatsNewModal() {
  const t = useT();
  // Si el user pertenece a un org con primary_color propio, respetarlo.
  // Si no (caso típico: superadmin sin org), usar teal HelixScan plataforma.
  const G = useAuthStore(s => s.user?.primary_color) || "#0891b2";
  const [visible, setVisible] = useState(false);
  const [info, setInfo]       = useState<VersionInfo | null>(null);
  const navigate = useNavigate();
  const isAdmin = useAuthStore(s => s.user?.role === "admin");
  // El detalle de versiones es interno: el popup "Novedades" solo lo ve el
  // superadmin (decisión Luis, 11 jun 2026). Las orgs ven solo el nº de versión.
  const isSuperAdmin = useAuthStore(s => s.user?.role === "superadmin");

  useEffect(() => {
    if (!isSuperAdmin) return;
    api.get<VersionInfo>("/version").then((data) => {
      const lastSeen = localStorage.getItem(LS_KEY);
      if (data.current_version !== lastSeen) {
        setInfo(data);
        setVisible(true);
      }
    }).catch(() => {/* silencioso si falla */});
  }, [isSuperAdmin]);

  const dismiss = () => {
    if (info) localStorage.setItem(LS_KEY, info.current_version);
    setVisible(false);
  };

  if (!visible || !info) return null;

  return (
    <>
      {/* Overlay */}
      <div
        onClick={dismiss}
        style={{
          position: "fixed", inset: 0, zIndex: 2000,
          background: "rgba(11,18,21,0.4)",
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}
      >
        {/* Modal card */}
        <div
          onClick={e => e.stopPropagation()}
          style={{
            background: "var(--clr-modal-solid)",
            borderRadius: 22,
            border: "1px solid var(--clr-border)",
            boxShadow: "0 24px 80px -24px rgba(0,0,0,0.45)",
            padding: "2rem 2.25rem",
            maxWidth: 480,
            width: "calc(100% - 2rem)",
            position: "relative",
          }}
        >
          {/* Header */}
          <div style={{ marginBottom: "1.25rem" }}>
            <div style={{ fontWeight: 700, fontSize: "1rem", letterSpacing: "-0.01em", color: "var(--clr-text)" }}>
              {t("whatsNew.title", { version: info.current_version })}
            </div>
            <div style={{ fontSize: "0.75rem", color: "var(--clr-text3)", marginTop: 3 }}>
              {info.current_date}
            </div>
          </div>

          {/* Divider */}
          <div style={{ height: 1, background: "var(--clr-border2)", marginBottom: "1.25rem" }} />

          {/* Highlights */}
          {info.current_highlights.length > 0 && (
            <ul style={{ margin: 0, paddingLeft: 0, listStyle: "none" }}>
              {info.current_highlights.map((h, i) => (
                <li
                  key={i}
                  style={{
                    display: "flex", gap: 10, alignItems: "flex-start",
                    marginBottom: 10, fontSize: "0.875rem", color: "var(--clr-text2)",
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 3, color: "var(--clr-text3)" }}>
                    <path d="M4.5 12.75l6 6 9-13.5" />
                  </svg>
                  <span>{renderInlineMarkdown(h)}</span>
                </li>
              ))}
            </ul>
          )}

          {/* Footer */}
          <div style={{ marginTop: "1.5rem", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
            {isAdmin ? (
              <button
                onClick={() => { dismiss(); navigate("/changelog"); }}
                style={{
                  background: "transparent", border: "none", cursor: "pointer",
                  color: G, fontSize: "0.8rem", fontWeight: 600, padding: 0,
                  textDecoration: "underline", textUnderlineOffset: 3,
                }}
              >
                {t("whatsNew.viewFullHistory")}
              </button>
            ) : <span />}
            <button
              onClick={dismiss}
              style={{
                padding: "0.6rem 1.5rem",
                borderRadius: 999,
                border: 0,
                background: G,
                color: "#fff",
                fontWeight: 600,
                fontSize: "0.875rem",
                cursor: "pointer",
              }}
            >
              {t("whatsNew.gotIt")}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
